import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import "./OrderForm.css";

const PRODUCTS = [
	{ slug: "cijfers_en_letters", title: "LED Cijfers & Letters", hasQty: true },
	{ slug: "verlichting", title: "Verlichting", hasQty: true },
	{ slug: "ballondecoratie", title: "Ballondecoratie", hasQty: false },
	{ slug: "photobooth", title: "Photobooth", hasQty: false },
	{
		slug: "dansvloer",
		title: "LED-Dansvloer",
		hasQty: false,
		sizes: ["3 × 3 m", "3 × 4 m", "4 × 4 m"],
	},
	{ slug: "anders", title: "Anders / nog niet zeker", hasQty: false },
];

const HOURS = Array.from({ length: 17 }, (_, i) => i + 6); // 6 to 22
const MINUTES = ["00", "15", "30", "45"];

function OrderForm({ mode = "aanvraag" }) {
	// "bestellen" = placing an order (terms required, order wording);
	// "aanvraag" = a non-binding request (no agreement checkbox).
	const isOrder = mode === "bestellen";
	const [searchParams] = useSearchParams();
	const [errors, setErrors] = useState({});

	const [products, setProducts] = useState(() =>
		PRODUCTS.reduce((acc, p) => {
			acc[p.slug] = {
				checked: false,
				quantity: 1,
				size: p.sizes ? p.sizes[0] : null,
			};
			return acc;
		}, {})
	);

	const [formData, setFormData] = useState({
		voornaam: "",
		achternaam: "",
		bedrijfsnaam: "",
		email: "",
		telefoon: "",
		adres: "",
		postcode_plaats: "",
		bezorg_adres: "",
		bezorg_postcode_plaats: "",
		datum: "",
		tijdstip: "",
		ophalen: "nee",
		beschrijving: "",
		privacyAccepted: false,
	});

	useEffect(() => {
		const slug = searchParams.get("product");
		if (slug && products[slug] !== undefined) {
			setProducts((prev) => ({
				...prev,
				[slug]: { ...prev[slug], checked: true },
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	const toggleProduct = (slug) => {
		setProducts((prev) => ({
			...prev,
			[slug]: { ...prev[slug], checked: !prev[slug].checked },
		}));
	};

	const setQuantity = (slug, quantity) => {
		setProducts((prev) => ({
			...prev,
			[slug]: { ...prev[slug], quantity },
		}));
	};

	const setSize = (slug, size) => {
		setProducts((prev) => ({
			...prev,
			[slug]: { ...prev[slug], size },
		}));
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
		if (errors[name]) setErrors({ ...errors, [name]: null });
	};

	const handleTimeChange = (e) => {
		const { name, value } = e.target;
		const currentTime = formData.tijdstip.split(":");
		if (name === "hour") {
			setFormData({
				...formData,
				tijdstip: `${value}:${currentTime[1] || "00"}`,
			});
		} else {
			setFormData({
				...formData,
				tijdstip: `${currentTime[0] || "06"}:${value}`,
			});
		}
	};

	const isPickup = formData.ophalen === "ja";

	const validatePostcodePlaats = (value) => {
		const postcodeRegex = /^[1-9][0-9]{3}\s?[A-Za-z]{2}$/;
		const postcodeValue = value.split(" ")[0];
		if (!postcodeRegex.test(postcodeValue)) {
			return "Voer een geldige postcode in (bijv. 1234AB)";
		}
		if (value.trim() === postcodeValue.trim()) {
			return "Voer ook de woonplaats in na de postcode, bijvoorbeeld 1234AB St. Willebrord";
		}
		return null;
	};

	const validateForm = () => {
		const newErrors = {};

		// The personal address is always required.
		const personalError = validatePostcodePlaats(formData.postcode_plaats);
		if (personalError) newErrors.postcode_plaats = personalError;

		// The delivery address only matters when we deliver.
		if (!isPickup) {
			const bezorgError = validatePostcodePlaats(
				formData.bezorg_postcode_plaats
			);
			if (bezorgError) newErrors.bezorg_postcode_plaats = bezorgError;
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const buildProductSummary = () => {
		const selected = PRODUCTS.filter((p) => products[p.slug].checked).map(
			(p) => {
				if (p.hasQty) {
					return `- ${p.title} (×${products[p.slug].quantity})`;
				}
				if (p.sizes) {
					return `- ${p.title} (${products[p.slug].size})`;
				}
				return `- ${p.title}`;
			}
		);
		if (selected.length === 0) return "";
		return `Aanvraag:\n${selected.join("\n")}\n\n`;
	};

	// idle | sending | success | error — drives the inline feedback + button.
	const [status, setStatus] = useState("idle");

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (status === "sending") return; // no double submits
		if (!validateForm()) return;
		setStatus("sending");

		const productSummary = buildProductSummary();
		const extraLines = [
			isOrder ? "Type: Bestelling" : "Type: Aanvraag",
			isPickup ? "Zelf ophalen: Ja" : "Zelf ophalen: Nee (bezorgen)",
			!isPickup
				? `Bezorglocatie: ${formData.bezorg_adres}, ${formData.bezorg_postcode_plaats}`
				: null,
			formData.bedrijfsnaam ? `Bedrijfsnaam: ${formData.bedrijfsnaam}` : null,
		]
			.filter(Boolean)
			.join("\n");
		const baseDescription = `${productSummary}${extraLines}\n\n`;
		const fullDescription = formData.beschrijving
			? `${baseDescription}Wensen:\n${formData.beschrijving}`
			: baseDescription;

		try {
			const response = await fetch(
				"https://api.aspectstudios.org/api/contacts/v4/contacts",
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						info: {
							name: {
								first: formData.voornaam,
								last: formData.achternaam,
							},
							emails: {
								items: [{ tag: "MAIN", email: formData.email }],
							},
							phones: {
								items: [
									{
										tag: "MAIN",
										countryCode: "NL",
										phone: formData.telefoon,
									},
								],
							},
							addresses: {
								items: [
									{
										tag: "HOME",
										address: {
											country: "NL",
											city: formData.postcode_plaats
												.split(" ")
												.slice(1)
												.join(" "),
											postalCode:
												formData.postcode_plaats.split(" ")[0],
											addressLine: formData.adres,
										},
									},
								],
							},
							...(formData.bedrijfsnaam
								? { company: formData.bedrijfsnaam }
								: {}),
							extendedFields: {
								items: {
									"custom.beschrijving": fullDescription,
									"custom.datum": formData.datum,
									"custom.tijdstip": formData.tijdstip,
								},
							},
							locale: "nl-nl",
						},
					}),
				}
			);

			if (!response.ok) {
				const errorData = await response.json();
				console.error("API Error:", errorData);
				throw new Error(
					`Failed to create contact: ${errorData.message || "Unknown error"}`
				);
			}

			setStatus("success");
			// reset
			setProducts(
				PRODUCTS.reduce((acc, p) => {
					acc[p.slug] = {
						checked: false,
						quantity: 1,
						size: p.sizes ? p.sizes[0] : null,
					};
					return acc;
				}, {})
			);
			setFormData({
				voornaam: "",
				achternaam: "",
				bedrijfsnaam: "",
				email: "",
				telefoon: "",
				adres: "",
				postcode_plaats: "",
				bezorg_adres: "",
				bezorg_postcode_plaats: "",
				datum: "",
				tijdstip: "",
				ophalen: "nee",
				beschrijving: "",
				privacyAccepted: false,
			});
		} catch (error) {
			console.error("Error:", error);
			setStatus("error");
		}
	};

	return (
		<form onSubmit={handleSubmit} className="order-form" noValidate>
			{/* Step 1 — Product selection */}
			<fieldset className="order-step">
				<div className="order-step-header">
					<span className="order-step-eyebrow">Stap 1</span>
					<h2 className="order-step-title">Wat heeft u nodig?</h2>
				</div>

				<div className="order-products">
					{PRODUCTS.map((p) => {
						const isChecked = products[p.slug].checked;
						return (
							<div
								key={p.slug}
								className={`order-product-row ${
									isChecked ? "is-checked" : ""
								}`}>
								<label className="order-product-label">
									<input
										type="checkbox"
										className="order-product-checkbox"
										checked={isChecked}
										onChange={() => toggleProduct(p.slug)}
									/>
									<span
										className="order-product-mark"
										aria-hidden="true"></span>
									<span className="order-product-title">{p.title}</span>
								</label>
								{isChecked && p.hasQty && (
									<div className="order-product-qty-wrap">
										<label
											htmlFor={`qty-${p.slug}`}
											className="order-product-qty-label">
											Aantal
										</label>
										<select
											id={`qty-${p.slug}`}
											className="order-product-qty"
											value={products[p.slug].quantity}
											onChange={(e) =>
												setQuantity(p.slug, parseInt(e.target.value, 10))
											}>
											{[...Array(15)].map((_, i) => (
												<option key={i + 1} value={i + 1}>
													{i + 1}
												</option>
											))}
										</select>
									</div>
								)}
								{isChecked && p.sizes && (
									<div className="order-product-qty-wrap">
										<label
											htmlFor={`size-${p.slug}`}
											className="order-product-qty-label">
											Maat
										</label>
										<select
											id={`size-${p.slug}`}
											className="order-product-qty"
											value={products[p.slug].size}
											onChange={(e) => setSize(p.slug, e.target.value)}>
											{p.sizes.map((size) => (
												<option key={size} value={size}>
													{size}
												</option>
											))}
										</select>
									</div>
								)}
							</div>
						);
					})}
				</div>
			</fieldset>

			{/* Step 2 — Your details */}
			<fieldset className="order-step">
				<div className="order-step-header">
					<span className="order-step-eyebrow">Stap 2</span>
					<h2 className="order-step-title">Uw gegevens</h2>
				</div>

				<div className="form-row">
					<div className="form-group">
						<label htmlFor="voornaam">Voornaam</label>
						<input
							type="text"
							id="voornaam"
							name="voornaam"
							className="order-input"
							value={formData.voornaam}
							onChange={handleChange}
							required
						/>
					</div>
					<div className="form-group">
						<label htmlFor="achternaam">Achternaam</label>
						<input
							type="text"
							id="achternaam"
							name="achternaam"
							className="order-input"
							value={formData.achternaam}
							onChange={handleChange}
							required
						/>
					</div>
				</div>

				<div className="form-row">
					<div className="form-group">
						<label htmlFor="email">E-mail</label>
						<input
							type="email"
							id="email"
							name="email"
							className="order-input"
							value={formData.email}
							onChange={handleChange}
							required
						/>
					</div>
					<div className="form-group">
						<label htmlFor="telefoon">Telefoonnummer</label>
						<input
							type="tel"
							id="telefoon"
							name="telefoon"
							className="order-input"
							value={formData.telefoon}
							onChange={handleChange}
							required
						/>
					</div>
				</div>

				<div className="form-group full-width">
					<label htmlFor="bedrijfsnaam">Bedrijfsnaam (optioneel)</label>
					<input
						type="text"
						id="bedrijfsnaam"
						name="bedrijfsnaam"
						className="order-input"
						value={formData.bedrijfsnaam}
						onChange={handleChange}
					/>
				</div>

				<div className="form-row">
					<div className="form-group">
						<label htmlFor="adres">Straatnaam + Huisnummer</label>
						<input
							type="text"
							id="adres"
							name="adres"
							className="order-input"
							value={formData.adres}
							onChange={handleChange}
							required
						/>
					</div>
					<div className="form-group">
						<label htmlFor="postcode_plaats">
							Postcode + Woonplaats
						</label>
						<input
							type="text"
							id="postcode_plaats"
							name="postcode_plaats"
							className={`order-input ${
								errors.postcode_plaats ? "input-error" : ""
							}`}
							value={formData.postcode_plaats}
							onChange={handleChange}
							placeholder="1234AB Sint Willebrord"
							required
						/>
						{errors.postcode_plaats && (
							<span className="error-message">
								{errors.postcode_plaats}
							</span>
						)}
					</div>
				</div>
			</fieldset>

			{/* Step 3 — When & where */}
			<fieldset className="order-step">
				<div className="order-step-header">
					<span className="order-step-eyebrow">Stap 3</span>
					<h2 className="order-step-title">Wanneer &amp; waar?</h2>
				</div>

				<div className="form-row">
					<div className="form-group">
						<label htmlFor="datum">Datum</label>
						<input
							type="date"
							id="datum"
							name="datum"
							value={formData.datum}
							onChange={handleChange}
							className="order-input"
							required
						/>
					</div>
					<div className="form-group">
						<label htmlFor="hour">Tijdstip</label>
						<div className="time-select-container">
							<select
								id="hour"
								name="hour"
								className="order-input"
								value={formData.tijdstip.split(":")[0] || ""}
								onChange={handleTimeChange}
								required>
								<option value="">Uur</option>
								{HOURS.map((hour) => (
									<option
										key={hour}
										value={hour.toString().padStart(2, "0")}>
										{hour.toString().padStart(2, "0")}
									</option>
								))}
							</select>
							<span className="time-sep" aria-hidden="true">
								:
							</span>
							<select
								name="minute"
								className="order-input"
								value={formData.tijdstip.split(":")[1] || ""}
								onChange={handleTimeChange}
								required>
								<option value="">Min</option>
								{MINUTES.map((minute) => (
									<option key={minute} value={minute}>
										{minute}
									</option>
								))}
							</select>
						</div>
					</div>
				</div>

				<div className="form-group full-width">
					<span className="order-radio-legend">Zelf ophalen?</span>
					<div
						className="order-radio-row"
						role="radiogroup"
						aria-label="Zelf ophalen?">
						<label className="order-radio">
							<input
								type="radio"
								name="ophalen"
								value="ja"
								checked={formData.ophalen === "ja"}
								onChange={handleChange}
							/>
							<span>Ja, ik haal het zelf op</span>
						</label>
						<label className="order-radio">
							<input
								type="radio"
								name="ophalen"
								value="nee"
								checked={formData.ophalen === "nee"}
								onChange={handleChange}
							/>
							<span>Nee, graag bezorgen</span>
						</label>
					</div>
					{isPickup && (
						<p className="order-pickup-hint">
							Ophaallocatie, Berkenhof 4 St. Willebrord
						</p>
					)}
					{!isPickup && (
						<div className="order-bezorg">
							<span className="order-bezorg-legend">Bezorglocatie</span>
							<div className="form-row">
								<div className="form-group">
									<label htmlFor="bezorg_adres">
										Straatnaam + Huisnummer
									</label>
									<input
										type="text"
										id="bezorg_adres"
										name="bezorg_adres"
										className="order-input"
										value={formData.bezorg_adres}
										onChange={handleChange}
										required
									/>
								</div>
								<div className="form-group">
									<label htmlFor="bezorg_postcode_plaats">
										Postcode + Woonplaats
									</label>
									<input
										type="text"
										id="bezorg_postcode_plaats"
										name="bezorg_postcode_plaats"
										className={`order-input ${
											errors.bezorg_postcode_plaats
												? "input-error"
												: ""
										}`}
										value={formData.bezorg_postcode_plaats}
										onChange={handleChange}
										placeholder="1234AB Sint Willebrord"
										required
									/>
									{errors.bezorg_postcode_plaats && (
										<span className="error-message">
											{errors.bezorg_postcode_plaats}
										</span>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			</fieldset>

			{/* Step 4 — Optional notes + submit */}
			<fieldset className="order-step">
				<div className="order-step-header">
					<span className="order-step-eyebrow">Stap 4</span>
					<h2 className="order-step-title">Aanvullende wensen</h2>
					<p className="order-step-hint">Optioneel</p>
				</div>

				<div className="form-group full-width">
					<label htmlFor="beschrijving">Beschrijving</label>
					<textarea
						id="beschrijving"
						name="beschrijving"
						className="order-input"
						rows="6"
						value={formData.beschrijving}
						onChange={handleChange}
						placeholder="Bijvoorbeeld: kleurenwens voor ballondecoratie, specifiek aantal letters, etc."
					/>
				</div>

				{isOrder && (
					<div className="privacy-section">
						<div className="checkbox-container">
							<input
								type="checkbox"
								id="privacy"
								name="privacyAccepted"
								checked={formData.privacyAccepted}
								onChange={(e) =>
									setFormData({
										...formData,
										privacyAccepted: e.target.checked,
									})
								}
								required
							/>
							<label htmlFor="privacy" className="privacy-label">
								Ik ga akkoord met het privacybeleid en bevestig dat ik de
								algemene voorwaarden heb gelezen en hiermee akkoord ga.
							</label>
						</div>
						<a
							href="/terms.pdf"
							target="_blank"
							rel="noopener noreferrer"
							className="pdf-link"
							aria-label="Algemene voorwaarden PDF">
							<i className="fas fa-file-pdf"></i>
						</a>
					</div>
				)}

				{status === "success" && (
					<p className="form-status form-status--success" role="status">
						{isOrder
							? "Bedankt voor uw bestelling! We nemen binnen 24 uur contact met u op ter bevestiging."
							: "Bedankt voor uw aanvraag! We nemen binnen 24 uur contact met u op."}
					</p>
				)}
				{status === "error" && (
					<p className="form-status form-status--error" role="alert">
						Er is iets misgegaan bij het versturen. Probeer het later
						opnieuw, of bel ons op 06-18218292.
					</p>
				)}

				<button
					type="submit"
					className="btn btn--primary btn--lg order-submit"
					disabled={status === "sending"}>
					{status === "sending"
						? "Versturen…"
						: isOrder
						? "Bestelling plaatsen"
						: "Verstuur aanvraag"}
				</button>
				<p className="order-reassurance">
					{isOrder ? (
						"Wij bevestigen uw bestelling binnen 24 uur"
					) : (
						<>
							Vrijblijvende aanvraag{" "}
							<span aria-hidden="true">·</span> 24/7 bereikbaar
						</>
					)}
				</p>
			</fieldset>
		</form>
	);
}

export default OrderForm;
